import React, { useState, useEffect } from 'react';
import { getLevelInfo } from '../services/level';

/**
 * 레벨 배지 컴포넌트
 * - 헤더/사용자 메뉴에서 레벨과 칭호를 표시
 */
const LevelBadge = ({ compact = false, showProgress = true, onClick }) => {
  const [levelInfo, setLevelInfo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchLevelInfo = async () => {
      try {
        const data = await getLevelInfo();
        setLevelInfo(data);
        setError(null);
      } catch (err) {
        console.error('Failed to fetch level info:', err);
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    fetchLevelInfo();
  }, []);

  if (loading) {
    return (
      <div className={`flex items-center px-3 py-1.5 bg-gradient-to-br from-slate-50 to-slate-100 border border-slate-200 rounded-xl cursor-pointer select-none transition-all duration-200 ${compact ? 'px-2.5 py-1 gap-1 bg-blue-500/[0.08] border-blue-500/20' : ''} pointer-events-none`}>
        <span className="block w-20 h-5 bg-gradient-to-r from-slate-100 via-slate-200 to-slate-100 bg-[length:200%_100%] animate-[skeleton-loading_1.5s_infinite] rounded"></span>
      </div>
    );
  }

  if (error || !levelInfo) {
    return null;
  }

  const { level, currentExp, nextLevelExp, progress, title } = levelInfo;

  if (compact) {
    return (
      <div
        className="flex items-center px-2.5 py-1 gap-1 bg-blue-500/[0.08] border border-blue-500/20 rounded-xl cursor-pointer select-none transition-all duration-200 hover:bg-blue-500/[0.12] hover:border-blue-500/30"
        onClick={onClick}
        role="button"
        tabIndex={0}
      >
        <span className="text-sm leading-none">{title?.icon || '👀'}</span>
        <span className="text-xs font-semibold text-blue-500">Lv.{level}</span>
      </div>
    );
  }

  return (
    <div
      className="flex items-center px-3 py-1.5 bg-gradient-to-br from-slate-50 to-slate-100 border border-slate-200 rounded-xl cursor-pointer select-none transition-all duration-200 hover:from-slate-100 hover:to-slate-200 hover:border-slate-300 hover:-translate-y-px hover:shadow-md active:translate-y-0 md:px-2 md:py-1"
      onClick={onClick}
      role="button"
      tabIndex={0}
    >
      <div className="flex items-center gap-2.5">
        <span className="text-2xl leading-none">{title?.icon || '👀'}</span>
        <div className="flex flex-col gap-1">
          <div className="flex items-center gap-2">
            <span className="text-sm font-bold text-gray-900">Lv.{level}</span>
            <span className="text-xs text-slate-500 font-medium">{title?.name || '문파 구경꾼'}</span>
          </div>
          {showProgress && (
            <div className="flex items-center gap-2">
              <div className="w-[100px] h-1.5 bg-slate-200 rounded-sm overflow-hidden md:w-[60px]">
                <div
                  className="h-full bg-gradient-to-r from-blue-500 to-blue-400 rounded-sm transition-[width] duration-300"
                  style={{ width: `${Math.min(progress, 100)}%` }}
                />
              </div>
              <span className="text-[10px] text-slate-400 font-medium whitespace-nowrap md:hidden">
                {currentExp.toLocaleString()} / {nextLevelExp.toLocaleString()}
              </span>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default LevelBadge;
